import React from "react";
import { render } from 'react-dom';
import { useEffect, useState, useRef, useReducer, useCallback, createRef } from "react";
import { ProductCustomizerReducer } from "../reducers/ProductCustomizerReducer";
import { ProductCustomizerState, CustomizerProduct, CustomizerFlow, CustomizerStep, CustomizerStepOption, CustomizerVariantValues, Variant } from "../types/ProductCustomizer";
import { ProductCustomizerModal } from "../components/ProductCustomizerModal";
import { productOptionToString } from '../utils/productOptionToString';
import { trapFocus } from '../utils/trapFocus';
import isEmptyObject from '../utils/isEmptyObject';


(function init() {
  const selectors = {
    root: '[data-product-customizer]'
  }

  const rootElement = document.querySelector(selectors.root);
  const baseProductHandle = rootElement?.getAttribute("data-product-handle");
  const baseProductFlow = rootElement?.getAttribute("data-product-flow");
  if (rootElement) render(<ProductCustomizer baseProductHandle={ baseProductHandle ? baseProductHandle : "very-wavy"} baseProductFlow={baseProductFlow} />, rootElement)
})();

function ProductCustomizer({ baseProductHandle, baseProductFlow}: { baseProductHandle: string, baseProductFlow: string }) {
  const selectors = {
    products: "[data-customizer-products]",
    flows: "[data-customizer-flows]",
    flows2: "[data-customizer-flows-2]",
    flows3: "[data-customizer-flows-3]"
  };

  /*
    Data from DOM
  */
 
  const flows: CustomizerFlow = JSON.parse(document.querySelector(selectors.flows).innerHTML)?.flows;
  const flows2: CustomizerFlow = JSON.parse(document.querySelector(selectors.flows2).innerHTML)?.flows;
  const flows3: CustomizerFlow = JSON.parse(document.querySelector(selectors.flows3).innerHTML)?.flows;
  const products: CustomizerProduct[] = JSON.parse(document.querySelector(selectors.products).innerHTML);

  /*
    Refs
  */
  const modal = createRef<HTMLDivElement>();
  const previousFocus = useRef<HTMLElement | null>(null);

  /*
    Set initial state
  */
  const urlSearchParams = new URLSearchParams(window.location.search);
  const initialState: ProductCustomizerState = {
    activeModal: urlSearchParams.has("flow") || false,
    flow: urlSearchParams.get("flow") || baseProductFlow,
    steps: [],
    selectedOptions: [],
    activeStep: parseInt(urlSearchParams.get("step"), 10) || 0,
    updatedOption: {
      step: null,
      option: null
    },
    variant: {},
    images: {}
  };
  const [state, dispatch] = useReducer(ProductCustomizerReducer, initialState);
  const [selectedProduct, setSelectedProduct] = useState<CustomizerProduct>();
  // console.log(state)

  /*
    Helpers
  */
  function queryStringParse(step: number, query: string): number {
    return state.steps[step].options.findIndex((el: CustomizerStepOption) => el.id === query);
  }

  function getVariantMatch(product: CustomizerProduct, selectedVariantValues: CustomizerVariantValues): Variant {
    
    const variantMatch = product.variants.find((el) => {
      return el.option1 === selectedVariantValues.option1 && el.option2 === selectedVariantValues.option2 && el.option3 === selectedVariantValues.option3;
    });
    return variantMatch;
  }

  function getVariantPriceDifference(variantOptionId: string, optionValue: string): number | null {
    let selectedVariantValues: CustomizerVariantValues = {
        option1: null,
        option2: null,
        option3: null
    };
//here

    selectedProduct.options.forEach((opt) => {
      // Check if this is the variant we need to simulate
      // console.log(opt.id)
      // console.log(variantOptionId)
      // console.log(optionValue)
   

      if (opt.id === variantOptionId) {
        selectedVariantValues[opt.id] = optionValue;
      } else {
        // Populate the remaining values based on selected options
        // OR if the variant options are not exposed in this flow, pre-populate them based on the currently selected variant
        const stepIndex = state.steps.findIndex((step) => step.id === opt.id);

        // If steps contain this variant, look for its value in state.selectedOptions
        if (stepIndex !== -1) {
          const optIndex = state.selectedOptions[stepIndex];
          if (typeof optIndex !== 'undefined') {
            const val = state.steps[stepIndex].options[optIndex].id;
            selectedVariantValues[opt.id] = val;
          }
        } else {
          // Else look at current selected variant and prefill the value of those options (so they basically don't change)
          selectedVariantValues[opt.id] = state.variant[opt.id];
        }
      }
    });

    if (selectedVariantValues.option1 !== null && selectedVariantValues.option2 !== null && selectedVariantValues.option3 !== null) {
      const variantMatch = getVariantMatch(selectedProduct, selectedVariantValues);
      if (variantMatch && state.variant) {
        const difference = variantMatch.price - state.variant?.price;
        if (difference > 0) return difference;
      }
    }

    return null;
  }

  function getProductPriceDifference(productHandle: string) {
    
    const targetProduct = products.find((product) => product.handle === productHandle);
    let difference: number = null;

    let selectedVariantValues: CustomizerVariantValues = {
        option1: null,
        option2: null,
        option3: null
    };

    selectedProduct.options.forEach((opt) => {
      const optionId = opt.id;
      const selectedValue = state.variant[optionId];
      const targetProductOption = targetProduct.options.find(el => el.id === optionId);

      if (targetProductOption.values.includes(selectedValue)) {
        selectedVariantValues[optionId] = selectedValue;
      } else {
        selectedVariantValues[optionId] = targetProductOption.values[0];
      }
    });

    const variantMatch = getVariantMatch(targetProduct, selectedVariantValues);

    if (variantMatch) {
      difference = variantMatch.price - state.variant?.price;
    }

    if (difference > 0) {
      return difference;
    }

    return null;
  }

  /*
    Build steps and selected options
  */
  function buildSteps(): CustomizerStep[] {
    let steps: CustomizerStep[] = [];
  
    // const flow: CustomizerStep[] = flows.find(((el: CustomizerStep[]) => el.title === state.flow)).steps;
    let flow: CustomizerStep[]=[]
    for (let i = 0 ; i < flows.length; i++){
      if(flows[i].title===state.flow)
        flow=flows[i].steps
    }
    for (let i = 0 ; i < flows2.length; i++){
      if(flows2[i].title===state.flow)
        flow=flows2[i].steps
    }

    for (let i = 0 ; i < flows3.length; i++){
      if(flows3[i].title===state.flow)
        flow=flows3[i].steps
    }



    flow.forEach((step: CustomizerStep) => {
      if (step.type === "variant") {
        const optionArray: CustomizerStepOption[] = [];
        const optionMatch = selectedProduct?.options.find(opt => opt.id === step.id);

        if (optionMatch) {
         
          optionMatch.values.forEach(val => {
            const contentMatch = step.options?.find(opt => val === opt.option_value);

            let optionValues: CustomizerStepOption = {
              id: val,
              label: val
            }

            if (contentMatch) {
              optionValues.option_value = val;
              optionValues.label = contentMatch.label ? contentMatch.label : val;
              optionValues.description = contentMatch.description;
              optionValues.image = contentMatch.image;
            }

            optionArray.push(optionValues);
          });

          step.options = optionArray;
          if (!step.title) step.title = optionMatch.name;
          steps.push(step)
        } else {
          // console.log("Product options don't match any steps, let's not output the step");
          return;
        }
      } else if (step.type === "line_item") {
        let optionArray: CustomizerStepOption[] = [];

        // Remove any disallowed options for selected variant
        if (state.variant) {
          step.options.forEach((opt, i) => {
            if (opt.disallow) {
              opt.disallow.forEach((el) => {
                const variantMatchesDisallowedValue = el.values.includes(state.variant[el.id]);
                if (!variantMatchesDisallowedValue) {
                  optionArray.push(opt);
                }
              });
            } else {
              optionArray.push(opt);
            }
          });
        }

        step.options = optionArray;
        steps.push(step);
      } else {
        steps.push(step);
      }
    });

    return steps;
  }

  function buildSelectedOptions(): (number | null)[] {
    let selectedOptions: (number | null)[] = [];

    state.steps.forEach((step: CustomizerStep, i: number) => {
      let valueToPush: number | null = 0;

      if (step.type === "content") {
        valueToPush = null;
      } else if (step.type === "line_item" || step.type === "display") {
        if (urlSearchParams.get(step.id)) {
          const match = queryStringParse(i, urlSearchParams.get(step.id));
          if (match > 0) valueToPush = match;
        } else if (step.default) {
          const match = queryStringParse(i, step.default);
          if (match > 0) valueToPush = match;
        }
      } else if (step.type === "variant") {
        
        const variant = selectedProduct?.variants.find(((el: Variant) => el.id === state.variant?.id));
        const selectedOptionForVariant = variant[step.id];
        const match = queryStringParse(i, selectedOptionForVariant);
        if (match > 0) valueToPush = match;

      } else if (step.type === "product") {
        const match = step.options?.findIndex((el: CustomizerStepOption) => el.id === selectedProduct?.handle);
        if (match && match > 0) valueToPush = match;
      }
      selectedOptions.push(valueToPush);
      
    });

    return selectedOptions;
  }

  function isSelectedProductVariant(variantId: number) {
    return selectedProduct?.variants.some((variant) => variant.id === variantId);
  }

  /*
    URL param functions
  */
  function addUrlParams() {
    urlSearchParams.set("flow", state.flow);
    urlSearchParams.set("step", state.activeStep.toString());

    state.selectedOptions.forEach((option: number | null, step: number) => {
      if (state.steps[step].type === "line_item" || state.steps[step].type === "display") {
        const { key, option_value } = productOptionToString(state.steps, step, option);
        urlSearchParams.set(key, option_value);
      }
    });

    window.history.replaceState({}, '', location.pathname + '?' + urlSearchParams);
  }

  function removeUrlParams() {
    urlSearchParams.delete("flow");
    urlSearchParams.delete("step");

    state.selectedOptions.forEach((option: number | null, step: number) => {
      const currentStep = state.steps[step];
      if (currentStep.type === "line_item" || currentStep.type === "display" ) {
        const { key } = productOptionToString(state.steps, step, option);
        urlSearchParams.delete(key);
      }
    });

    window.history.replaceState({}, '', location.pathname + "?" + urlSearchParams);
  }



  useEffect(() => {
    if (state.activeModal) {
      addUrlParams();
      document.body.classList.add("overflow-hidden", "fixed", "w-full");
      previousFocus.current = (document.activeElement as HTMLElement) ?? null;
    } else {
      removeUrlParams();
      document.body.classList.remove("overflow-hidden", "fixed", "w-full");
      previousFocus.current?.focus?.();
      previousFocus.current = null;
    }
  }, [state.activeModal]);


  /*
    1. On mount, set product based on handle
    2. On product change, set variant based on URL OR default value specified in steps OR first variant
    3. Build steps
    4. Build selected options
    5. Listen for changes to selected options: update URL params, update variant
  */

  // 1. On mount, set product based on handle
  useEffect(() => {
    const baseProduct = products.find((product) => product.handle === baseProductHandle);
    setSelectedProduct(baseProduct);

    // Escape key handler
    function onEscapeKey(event: any): void {
      if (!state.activeModal) return;
      if (event.code.toUpperCase() === "ESCAPE") {
        dispatch({
          type: "activeModal",
          value: !state.activeModal
        });
      }
    };
    document.addEventListener("keydown", (event) => onEscapeKey(event));
    return () => {
      document.removeEventListener("keydown", (event) => onEscapeKey(event));
    };
  }, []);

  // 2. On product change, check for variant ID in URL, find matching variant OR select default value specified in steps OR first variant
  useEffect(() => {
    if (selectedProduct) {
      const steps = buildSteps();
      let selectedVariant: Variant = selectedProduct?.variants[0];
      let urlParamId = urlSearchParams.get("variant") ? parseInt(urlSearchParams.get("variant"), 10) : null;

      let selectedVariantValues: CustomizerVariantValues = {
        option1: null,
        option2: null,
        option3: null
      };

      // Check there's a variant ID in the URL
      if (urlParamId) {
        if (isSelectedProductVariant(urlParamId)) {
          // This variant ID exists for the product, select that variant
          selectedVariant = selectedProduct.variants.find(el => el.id === urlParamId);
        } else {
          // This variant ID does not exist for the product, check if it's a valid variant ID for any other products
          let variantMatchFromUrlParamId;
          products.some((product) => {
            variantMatchFromUrlParamId = product.variants.find(variant => variant.id === urlParamId);
            if (variantMatchFromUrlParamId) return variantMatchFromUrlParamId;
          });

          // If this is NOT a valid variant ID for any products, select first variant for product
          if (!variantMatchFromUrlParamId) {
    
            selectedVariant = selectedProduct?.variants[0];
          } else {
            // It is a valid variant ID, check what option values it corresponds to and match that to a variant in the now-selected product
            selectedProduct.options.forEach((opt) => {
              const optionId = opt.id;
              const selectedValue = variantMatchFromUrlParamId[optionId];
              const targetProductOption = selectedProduct.options.find(el => el.id === optionId);
             

              if (targetProductOption.values.includes(selectedValue)) {
                selectedVariantValues[optionId] = selectedValue;
              } else {
                selectedVariantValues[optionId] = targetProductOption.values[0];
              }
            });

            const variantMatch = getVariantMatch(selectedProduct, selectedVariantValues);
            if (variantMatch) selectedVariant = variantMatch;
          }
        }
      } else {
        // Else pre-select default specified in steps OR first variant
        selectedProduct.options.forEach((opt) => {
          if (opt.values.length > 1) {
            const step = steps.find((step) => step.id === opt.id);

            if (step) {
              if (step.default) {
                const isValidOptionForVariant = step.options.find((el) => el.label === step.default);
                selectedVariantValues[step.id] = isValidOptionForVariant ? step.default : step.options[0].label;
              } else {
                selectedVariantValues[step.id] = step.options[0].label;
              }
            }
          }
        });

        const variantMatch = getVariantMatch(selectedProduct, selectedVariantValues);
        if (variantMatch) selectedVariant = variantMatch;
      }

      dispatch({ type: "variant", value: selectedVariant });
    }
  }, [selectedProduct]);

  // 3. Build steps
  useEffect(() => {
    if (!isEmptyObject(state.variant)) {
      const steps = buildSteps();
      if (steps) dispatch({ type: "steps", value: steps });
    }
  }, [state.variant]);

  // 4. Build selected options
  useEffect(() => {
    if (state.steps.length > 0) {
      const selectedOptions = buildSelectedOptions();
      dispatch({ type: "resetSelectedOptions", value: selectedOptions });

      if (!isEmptyObject(state.variant)) {
        urlSearchParams.set("variant", state.variant?.id.toString());
        window.history.replaceState({}, '', location.pathname + '?' + urlSearchParams);
      }
    }
  }, [state.steps]);

  // 5. Listen for changes to selected options: update URL params, update variant
  useEffect(() => {
    if (state.selectedOptions.length > 0) {
      // a. Check line items
      state.selectedOptions.forEach((option: number | null, step: number) => {
        const currentStep = state.steps[step];
        if (currentStep.type === "line_item" || currentStep.type === "display") {
          const { key, option_value } = productOptionToString(state.steps, step, option);
          urlSearchParams.set(key, option_value);
        }
      });

      // b. Check variant options, allowing for a flow that doesn't expose all variant options
      let selectedVariantValues: CustomizerVariantValues = {
          option1: null,
          option2: null,
          option3: null
      };

      selectedProduct.options.forEach((opt) => {
        const stepIndex = state.steps.findIndex((step) => step.id === opt.id);

        // If steps contain this variant, look for its value in state.selectedOptions
        if (stepIndex !== -1) {
          const optIndex = state.selectedOptions[stepIndex];
          const val = state.steps[stepIndex].options[optIndex].id;
          selectedVariantValues[opt.id] = val;
        } else {
          // Else look at current selected variant and prefill the value of those options (so they basically don't change)
          selectedVariantValues[opt.id] = state.variant[opt.id];
        }
      });

      // Update URL and state with variant match
      if (state.activeModal) window.history.replaceState({}, '', location.pathname + '?' + urlSearchParams);
      const variantMatch = getVariantMatch(selectedProduct, selectedVariantValues);
      if (variantMatch) dispatch({ type: "variant", value: variantMatch })
    }
  }, [state.selectedOptions]);

  useEffect(() => {
    if (state.activeModal) {
      urlSearchParams.set("step", state.activeStep.toString());
      window.history.replaceState({}, '', location.pathname + '?' + urlSearchParams);
    }
  }, [state.activeStep]);

  useEffect(() => {
    if (state.activeModal) {
      // Hack to gather all focusable elements each time the modal content changes and trap focus within those elements
      setTimeout(function () {
        const customizer = document.querySelector("#customizer");
        if (customizer) trapFocus(customizer);
      }, 100);
    }
  }, [state.activeModal, state.activeStep])

  /*
    Handlers
  */
  const handleFieldChange = useCallback((key: string, value: string) => {
    dispatch({ type: key, value: value })
  }, []);

  const handleOptionChange = useCallback((key: string, index: number, value: string) => {

    dispatch({ type: key, index: index, value: value })
  }, []);

  const handleProductChange = useCallback((handle: string) => {
    const product = products.find((product) => product.handle === handle);
    if (product) setSelectedProduct(product);
  }, []);

  const handleUpdatedOption = useCallback((step, option) => {
    const value = {
      step,
      option
    }
    dispatch({ type: "updatedOption", value: value });
  }, []);


  return (
    <>
      {state.flow=="pmp" ?      <button
        className="bg-black text-white font-bold uppercase text-sm w-full mt-5 px-3 py-[1.125rem]"
        onClick={() => {
          dispatch({ type: 'activeModal', value: !state.activeModal })
        }}
      >
       Next
      </button>:<button
        className="bg-black text-white font-medium uppercase text-sm w-full px-3 py-[1.125rem]"
        onClick={() => {
          dispatch({ type: 'activeModal', value: !state.activeModal })
        }}
      >
       Customize This Wig
      </button>}


      {selectedProduct && state.variant && state.activeModal && <ProductCustomizerModal ref={modal} state={state} handleFieldChange={handleFieldChange} handleOptionChange={handleOptionChange} handleProductChange={handleProductChange} product={selectedProduct} getVariantPriceDifference={getVariantPriceDifference} getProductPriceDifference={getProductPriceDifference}  handleUpdatedOption={handleUpdatedOption} dispatch={dispatch} />}
 
    </>
    

  )
};